import React from 'react'
import {
    PNTopicAccordionContainer,
    StyledChevron,
    StyledTopic,
    TopicDivider,
    SecondaryTopicsContainer,
    ChevronContainer,
    TopicChevronAccordion,
    SecondaryTopicsItem,
    SecondaryTopicsWrapper,
    StyledLink,
    AccordionWrapper,
    StyledTopicLink,
} from './PNTopicAccordion.styled'
import { PNTopicAccordionProps } from './PNTopicAccordion.routing'
import { RoutablePerthNowSection } from '@west-australian-newspapers/publication-types'
import { NavItem } from '@news-mono/web-common'

export const PNTopicAccordion: React.FC<PNTopicAccordionProps> = ({
    topics,
    perthNowSectionNavItems,
    subTopicNavItems,
    topicHeaderType,
}) => {
    const [showSecondaryTopics, setShowSecondaryTopics] = React.useState(false)
    const { primary } = topics

    // Build the topic chain from the primary topic to the root
    const topicChain = [primary]
    let observedTopic = primary
    while (observedTopic.parent) {
        topicChain.unshift(observedTopic.parent)
        observedTopic = observedTopic.parent
    }

    const currentTopic = topicChain[topicChain.length - 1]
    const primaryTopic = topicChain[0]
    const primaryTopicId = primaryTopic.id.replace(/-/g, '')

    // Assign the parent topic based on the primary topic ID
    let parentTopic: NavItem | undefined
    if (primaryTopicId in subTopicNavItems) {
        parentTopic =
            perthNowSectionNavItems[primaryTopicId as RoutablePerthNowSection]
    }

    // No parent topic, so we can't build the accordion
    if (!parentTopic) {
        return null
    }

    const topicChainLength = topicChain.length - 1
    const navItems: NavItem[] = []

    // Build out navItems list
    if (topicChainLength === 1 && parentTopic) {
        navItems.push(parentTopic)
    } else {
        let observedIndex = 0

        // Loop through the topic chain starting from the second item (index 1) as the first item
        while (observedIndex < topicChainLength) {
            // If it's the first sub topic, we want to check the parentTopic, else check the previous topic in the chain.
            const previousNavIndex = observedIndex - 1
            const observedTopic = topicChain[observedIndex]

            // No previous nav index, so likely it's the first item in the chain
            if (previousNavIndex < 0) {
                navItems.push(parentTopic)
            } else {
                const previousNavItem = navItems[previousNavIndex]

                // Check the children of the previousNavItem
                if (previousNavItem && observedTopic) {
                    const subNavLinks = previousNavItem.subNavLinks ?? []
                    const foundSubTopic = subNavLinks.find(
                        (item) => item.link === `/${observedTopic.id}`,
                    )

                    if (foundSubTopic) {
                        navItems.push(foundSubTopic)
                    } else {
                        console.warn(
                            { observedTopic, previousNavItem, subNavLinks },
                            'Unable to find sub topic for',
                        )
                    }
                } else {
                    console.warn(
                        {
                            observedIndex,
                            parentTopic,
                            navItems,
                            previousNavItem,
                        },
                        'Unable to find previous nav item or observed topic',
                    )
                }
            }

            observedIndex++
        }
    }

    // current topic on a topic page
    const lastTopic = navItems[navItems.length - 1]
    const secondaryTopics = (lastTopic?.subNavLinks ?? []).filter(
        (topic) =>
            topic.hideFromMainNav === undefined || !topic.hideFromMainNav,
    )
    const currentPageTopic = secondaryTopics.find(
        (topic) => topic.link === `/${currentTopic.id}`,
    )

    return (
        <PNTopicAccordionContainer
            aria-label="Accordion Navigation"
            isTopicPage={!!topicHeaderType}
        >
            <AccordionWrapper>
                {
                    // Sub topics display
                    // Parent | Sub Topic | Sub Topic
                    navItems.map((item, index) => {
                        const isFinalItem = index === navItems.length - 1

                        if (isFinalItem) {
                            return (
                                <TopicChevronAccordion
                                    key={index}
                                    onClick={() =>
                                        setShowSecondaryTopics(
                                            !showSecondaryTopics,
                                        )
                                    }
                                    tabIndex={0}
                                    aria-expanded={showSecondaryTopics}
                                >
                                    <StyledTopic isOpen={showSecondaryTopics}>
                                        {item.name}
                                    </StyledTopic>
                                </TopicChevronAccordion>
                            )
                        } else {
                            return (
                                <>
                                    <StyledTopicLink
                                        key={index}
                                        isOpen={showSecondaryTopics}
                                        to={item.link}
                                    >
                                        {item.name}
                                    </StyledTopicLink>
                                    <TopicDivider aria-hidden="true" />
                                </>
                            )
                        }
                    })
                }
                {secondaryTopics.length > 0 && (
                    <ChevronContainer
                        onClick={() =>
                            setShowSecondaryTopics(!showSecondaryTopics)
                        }
                    >
                        <StyledChevron isOpen={showSecondaryTopics} />
                    </ChevronContainer>
                )}
            </AccordionWrapper>
            {/* If there is secondary topics, we want to display them on drop-down when the showSecondaryTopics is true */}
            {secondaryTopics.length > 0 && (
                <SecondaryTopicsWrapper isOpen={showSecondaryTopics}>
                    <SecondaryTopicsContainer isOpen={showSecondaryTopics}>
                        {secondaryTopics.map((topic, index) => {
                            const isCurrent =
                                topic.name === currentPageTopic?.name

                            return (
                                <SecondaryTopicsItem
                                    isCurrent={isCurrent}
                                    key={index}
                                >
                                    <StyledLink
                                        to={`${topic.link}`}
                                        aria-current={
                                            isCurrent ? 'location' : undefined
                                        }
                                        tabIndex={showSecondaryTopics ? 0 : -1}
                                    >
                                        {topic.name}
                                    </StyledLink>
                                </SecondaryTopicsItem>
                            )
                        })}
                    </SecondaryTopicsContainer>
                </SecondaryTopicsWrapper>
            )}
        </PNTopicAccordionContainer>
    )
}
